/**
 * sessionBridge.js — one way to say "the session is over" from code that is not a component.
 * =================================================================================================
 *
 * ⭐ THE BUG THIS CLOSES (2026-10-24, found by the operator in production). The app had TWO sources
 * of truth for "signed in": the token in localStorage, which expires and is self-cleaning, and the
 * Redux `user` slice, which is neither. So a tab left open past the 12-hour token life reached a
 * state where the token had been removed and the app still knew who you were — and a component that
 * gates on `currentUser` happily fired an authenticated request with no credentials.
 *
 * Measured: two breadcrumbs, `/architecture/litgroups` at 06:06 and 06:18 UTC, both
 * `sentAuthHeader: false` with `serverReason: no-credentials`, twelve hours after a late-morning
 * sign-in. ⛔ And the diagnostic panel then told the operator his session had "probably not expired"
 * — the one thing it certainly had done.
 *
 * ─── ⛔ WHY A REGISTRY AND NOT AN IMPORT ────────────────────────────────────────────────────────
 *
 * The honest fix is for the places that clear a dead token to empty the store as well. But
 * `apiClient` and `useIsLoggedIn` cannot import the store: the store imports the slices, the slices
 * import `apiClient`, and closing that loop is an import cycle whose symptom is an undefined
 * reducer at boot. So the STORE registers itself here (it already imports nothing from this file's
 * callers), and the callers import only this module, which imports nothing at all.
 *
 * ⓘ Unregistered is a no-op, deliberately. A test that renders a component without a Provider, or
 * any code path that runs before the store exists, must not crash on the way to clearing a token.
 */

let ender = null;

/**
 * Called once, by the store, as soon as it exists.
 *
 * ⚠️ Last registration wins. There is one store; a second call means a second store, which is a
 * test harness or a mistake, and in both cases the newest one is the one that matters.
 */
export function registerSessionEnder(fn) {
  ender = typeof fn === 'function' ? fn : null;
}

/**
 * Empty the store because the credential is gone.
 *
 * ⚠️ NEVER THROWS. This is called from a token-clearing path, and a failure to reset the store must
 * not leave the token behind — the credential going is the part that matters, and a stale slice is
 * a smaller problem than a live token nobody meant to keep.
 */
export function endSession(why) {
  if (!ender) return false;
  try {
    ender(why);
    return true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('sessionBridge: store reset failed (' + (why || 'no reason given') + ')', e);
    return false;
  }
}

export default { registerSessionEnder, endSession };
